import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingScreen from '@sophocles/components/LoadingScreen';
import { formatDateTime } from '@sophocles/utils/formatUtils';

const CicStatusDisplay = props => {
  const { t } = useTranslation();
  const [hasStatus, setHasStatus] = useState(true);

  useEffect(() => {
    const { unitId, fetchStatus } = props;
    fetchStatus(unitId).then(res => {
      if (res.cicStatuses.size === 0) {
        setHasStatus(false);
      }
    });
  }, [props.unitId, props.fetchStatus]);

  const { isCurrent, status, close, showCurrent, unitTimeZone, user } = props;

  return (
    <div style={{ height: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: 'row-reverse',
        }}
      >
        <IconButton
          iconClassName="far fa-times-circle"
          iconStyle={{ color: 'var(--text-secondary)' }}
          onClick={close}
        />
        <Button color={isCurrent ? 'secondary' : 'primary'} onClick={showCurrent}>
          {t('sites.cic.showCurrentStatus', 'Show Current Status')}
        </Button>
      </div>

      {status == null || status.get('iccid', undefined) === undefined ? (
        hasStatus ? (
          <LoadingScreen />
        ) : (
          <h4 style={{ textAlign: 'center' }}>
            {t('sites.cic.noStatusFound', 'No Status Found, what are you doing here?')}
          </h4>
        )
      ) : (
        <div style={{ overflowY: 'auto', display: 'block', height: 'calc(100% - 90px)' }}>
          <FieldEntry
            title={
              isCurrent
                ? t('sites.cic.statusId', 'Status ID')
                : t('sites.cic.historicalStatusId', 'Historical Status ID')
            }
            value={status.get('id')}
          />
          <FieldEntry title={t('sites.cic.cicId', 'CIC ID')} value={status.get('cic')} />
          <FieldEntry title={t('sites.cic.iccid', 'ICCID')} value={status.get('iccid')} />
          <FieldEntry
            title={t('sites.cic.lastReceiveTimeYourTz', 'Last Receive Time (Your TZ)')}
            value={
              status.has('last_receive_time') ? formatDateTime(new Date(status.get('last_receive_time')), user) : null
            }
          />
          <FieldEntry
            title={t('sites.cic.lastReceiveTimeUnitTz', 'Last Receive Time (Unit TZ)')}
            value={
              status.has('last_receive_time')
                ? formatDateTime(toZonedTime(status.get('last_receive_time'), unitTimeZone), user)
                : null
            }
          />
          <FieldEntry
            title={t('sites.cic.receiveTimeYourTz', 'Receive Time (Your TZ)')}
            value={status.has('receive_time') ? formatDateTime(new Date(status.get('receive_time')), user) : null}
          />
          <FieldEntry
            title={t('sites.cic.receiveTimeUnitTz', 'Receive Time (Unit TZ)')}
            value={
              status.has('receive_time')
                ? formatDateTime(toZonedTime(status.get('receive_time'), unitTimeZone), user)
                : null
            }
          />
          <FieldEntry
            title={t('sites.cic.checkInTime', 'Check In Time')}
            value={status.get('check_in_time', null) || 'null'}
          />
          <FieldEntry
            title={t('sites.cic.unitTime', 'Unit Time')}
            value={formatDateTime(status.get('unit_time', null), user)}
          />
          <FieldEntry
            title={t('sites.cic.cpuTemperature', 'CPU Temperature (°C)')}
            value={roundOrNull(status.get('cpu_temperature', null), 3)}
          />
          <FieldEntry
            title={t('sites.cic.modemTemperature', 'Modem Temperature (°C)')}
            value={roundOrNull(status.get('modem_temperature', null), 3)}
          />
          <FieldEntry title={t('sites.cic.inputVoltage', 'Input Voltage (V)')} value={status.get('input_voltage')} />
          <FieldEntry title={t('sites.cic.rssi', 'RSSI')} value={status.get('rssi')} />
          <FieldEntry
            title={t('sites.cic.videoDetected', 'Video Detected')}
            value={joinOrNull(status.get('video_detected', null))}
          />
          <FieldEntry
            title={t('sites.cic.alarmEnabled', 'Alarm Enabled')}
            value={joinOrNull(status.get('alarm_enabled', null))}
          />
          <FieldEntry title={t('sites.cic.alarmDelay', 'Alarm Delay')} value={status.get('alarm_delay')} />
          <FieldEntry title={t('sites.cic.alarmInterval', 'Alarm Interval')} value={status.get('alarm_interval')} />
          <FieldEntry
            title={t('sites.cic.alarmPhotoCount', 'Alarm Photo Count')}
            value={status.get('alarm_photo_count')}
          />
          <FieldEntry
            title={t('sites.cic.minTimeBetweenAlarms', 'Minimum Time Between Alarms')}
            value={status.get('minimum_time_between_alarms')}
          />
          <FieldEntry
            title={t('sites.cic.dataSessionTimeout', 'Data Session Timeout')}
            value={status.get('data_session_timeout')}
          />
        </div>
      )}
    </div>
  );
};

const FieldEntry = ({ title, value, show }) => {
  return show && value !== null ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'middle' }}>
      <div style={{ fontWeight: 'bold', flex: '50% 0 0', textAlign: 'right' }}>
        <span style={{ paddingRight: '5px' }}>{title}</span>
      </div>
      <div style={{ flex: '50% 0 0' }}>
        <span style={{ paddingLeft: '5px' }}>{value}</span>
      </div>
    </div>
  ) : null;
};

function joinOrNull(arr) {
  return arr !== null ? arr.map(x => roundOrNull(x, 4)).join(', ') : null;
}

function roundOrNull(value, precision) {
  return value == null ? null : _.round(value, precision);
}

export default CicStatusDisplay;
